import type { RichTextField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { cn } from '@/lib/utils';
import { OpenLinkIcon } from './OpenLinkIcon';

export type StatementBoxProps = {
  title?: RichTextField | null;
  body?: RichTextField | null;
  isDark?: boolean;
};

/**
 * Purple-left-accent statement box. Reuses the same card treatment as
 * `ProblemHighlightGrid.redesign` (rounded gray tile with a `#4b0fea` left
 * border) and lifts the bulleted body into a proper list serializer so
 * inline links can render with an external-link icon suffix.
 */
export function StatementBox({ title, body, isDark }: StatementBoxProps) {
  const hasTitle = isFilled.richText(title);
  const hasBody = isFilled.richText(body);

  if (!hasTitle && !hasBody) {
    return null;
  }

  return (
    <div
      className={cn(
        'w-full rounded-lg px-4 py-4 md:px-5 md:py-5',
        isDark ? 'bg-white/[0.06]' : 'bg-[#f3f3f3]',
      )}
    >
      <div className="flex flex-col gap-3">
        {hasTitle && (
          <PrismicRichText
            field={title}
            components={{
              heading3: ({ children }) => (
                <h3
                  className={cn(
                    'border-l border-[#4b0fea] pl-3 text-[22px] font-semibold leading-[1.4]',
                    isDark ? 'text-white' : 'text-[#041118]',
                  )}
                >
                  {children}
                </h3>
              ),
            }}
          />
        )}

        {hasBody && (
          <PrismicRichText
            field={body}
            components={{
              paragraph: ({ children }) => (
                <p
                  className={cn(
                    'pl-3 text-[15px] font-light leading-[1.6] md:text-[16px] [&:not(:first-child)]:mt-3',
                    isDark ? 'text-white/80' : 'text-[#041118]',
                  )}
                >
                  {children}
                </p>
              ),
              list: ({ children }) => (
                <ul
                  className={cn(
                    'list-disc pl-8 text-[15px] font-light leading-[1.7] md:text-[16px]',
                    isDark ? 'text-white/80' : 'text-[#041118]',
                  )}
                >
                  {children}
                </ul>
              ),
              listItem: ({ children }) => (
                <li className="[&:not(:first-child)]:mt-1">{children}</li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">{children}</strong>
              ),
              hyperlink: ({ node }) => (
                <a
                  href={node.data.url ?? '#'}
                  className="ml-2 inline-flex items-center align-baseline text-[#4b0fea] hover:opacity-80"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={node.data.url ?? 'Open link'}
                >
                  <OpenLinkIcon className="inline-block h-[24px] w-[24px] shrink-0 translate-y-[5px]" />
                </a>
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}
